/**
 * VisibilityRulesEditor - Component for editing visibility rules on canvas elements.
 *
 * Allows users to define conditions that control when an element is visible.
 * Supports AND/OR logic with multiple conditions.
 */

import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Stack,
  FormControl,
  FormLabel,
  Select,
  Option,
  Input,
  IconButton,
  Button,
  RadioGroup,
  Radio,
  Chip,
} from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import type {
  VisibilityRules,
  VisibilityCondition,
  VisibilityOperator,
  CanvasDataSource,
  ViewField,
} from "../../types";

/**
 * Operator labels for display.
 */
const OPERATOR_LABELS: Record<VisibilityOperator, string> = {
  eq: "equals",
  ne: "not equals",
  gt: "greater than",
  lt: "less than",
  gte: "greater or equal",
  lte: "less or equal",
  in: "is in",
  not_in: "is not in",
  is_empty: "is empty",
  is_not_empty: "is not empty",
};

/**
 * Operators that don't require a value.
 */
const NO_VALUE_OPERATORS: VisibilityOperator[] = ["is_empty", "is_not_empty"];

/**
 * Field option derived from data sources and their view fields.
 */
interface FieldOption {
  value: string;
  label: string;
  dataSourceId: string;
  dataSourceName: string;
}

interface VisibilityRulesEditorProps {
  rules: VisibilityRules | undefined;
  onChange: (rules: VisibilityRules | undefined) => void;
  dataSources: CanvasDataSource[];
  /** Cache of view fields by view ID */
  viewFieldsCache?: Record<string, ViewField[]>;
}

/**
 * VisibilityRulesEditor - Rule editor with AND/OR logic and multiple conditions.
 */
export function VisibilityRulesEditor({
  rules,
  onChange,
  dataSources,
  viewFieldsCache = {},
}: VisibilityRulesEditorProps) {
  // Build list of available fields from data sources
  const fieldOptions = useMemo((): FieldOption[] => {
    const options: FieldOption[] = [];

    dataSources.forEach((ds) => {
      const viewId = ds.view_id ? String(ds.view_id) : null;
      const fields = viewId ? viewFieldsCache[viewId] || [] : [];

      fields.forEach((field) => {
        const fieldLabel =
          field.display_name ||
          field.column?.display_name ||
          field.alias ||
          field.column?.column_name ||
          "Unnamed Field";

        options.push({
          value: `${ds.id}.${field.id}`,
          label: fieldLabel,
          dataSourceId: ds.id,
          dataSourceName: ds.name,
        });
      });

      // If no fields loaded yet, add a placeholder
      if (fields.length === 0 && viewId) {
        options.push({
          value: `${ds.id}._placeholder`,
          label: "(Loading fields...)",
          dataSourceId: ds.id,
          dataSourceName: ds.name,
        });
      }
    });

    return options;
  }, [dataSources, viewFieldsCache]);

  // Initialize rules if empty - memoize to prevent unnecessary re-renders
  const currentRules: VisibilityRules = useMemo(
    () =>
      rules || {
        logic: "and",
        conditions: [],
      },
    [rules],
  );

  const handleLogicChange = (logic: "and" | "or") => {
    onChange({
      ...currentRules,
      logic,
    });
  };

  const handleAddCondition = () => {
    const newCondition: VisibilityCondition = {
      field: fieldOptions[0]?.value || "",
      operator: "eq",
      value: "",
    };

    onChange({
      ...currentRules,
      conditions: [...currentRules.conditions, newCondition],
    });
  };

  const handleUpdateCondition = (
    index: number,
    updates: Partial<VisibilityCondition>,
  ) => {
    const newConditions = [...currentRules.conditions];
    newConditions[index] = { ...newConditions[index], ...updates };

    // Clear value if switching to a no-value operator
    if (updates.operator && NO_VALUE_OPERATORS.includes(updates.operator)) {
      newConditions[index].value = undefined;
    }

    onChange({
      ...currentRules,
      conditions: newConditions,
    });
  };

  const handleRemoveCondition = (index: number) => {
    const newConditions = currentRules.conditions.filter((_, i) => i !== index);

    // If no conditions left, clear the rules entirely
    if (newConditions.length === 0) {
      onChange(undefined);
    } else {
      onChange({
        ...currentRules,
        conditions: newConditions,
      });
    }
  };

  const handleClearRules = () => {
    onChange(undefined);
  };

  // Generate preview text
  const previewText = useMemo(() => {
    if (!currentRules.conditions.length) {
      return "Always visible";
    }

    const conditionTexts = currentRules.conditions.map((cond) => {
      const fieldOption = fieldOptions.find((f) => f.value === cond.field);
      const fieldName = fieldOption?.label || cond.field;
      const opLabel = OPERATOR_LABELS[cond.operator];

      if (NO_VALUE_OPERATORS.includes(cond.operator)) {
        return `${fieldName} ${opLabel}`;
      }

      const valueDisplay =
        typeof cond.value === "string" ? `'${cond.value}'` : String(cond.value);
      return `${fieldName} ${opLabel} ${valueDisplay}`;
    });

    const joiner = currentRules.logic === "and" ? " AND " : " OR ";
    return `Show when: ${conditionTexts.join(joiner)}`;
  }, [currentRules, fieldOptions]);

  return (
    <Stack spacing={2}>
      {/* Logic selector */}
      {currentRules.conditions.length > 1 && (
        <FormControl>
          <FormLabel>Match</FormLabel>
          <RadioGroup
            orientation="horizontal"
            value={currentRules.logic}
            onChange={(e) => handleLogicChange(e.target.value as "and" | "or")}
          >
            <Radio value="and" label="All conditions (AND)" size="sm" />
            <Radio value="or" label="Any condition (OR)" size="sm" />
          </RadioGroup>
        </FormControl>
      )}

      {/* Conditions list */}
      {currentRules.conditions.map((condition, index) => (
        <Box
          key={index}
          sx={{
            p: 1.5,
            borderRadius: "sm",
            bgcolor: "background.level1",
            border: "1px solid",
            borderColor: "neutral.outlinedBorder",
          }}
        >
          <Stack spacing={1.5}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                Condition {index + 1}
              </Typography>
              <IconButton
                size="sm"
                variant="plain"
                color="danger"
                onClick={() => handleRemoveCondition(index)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>

            {/* Field selector */}
            <FormControl size="sm">
              <FormLabel>Field</FormLabel>
              <Select
                value={condition.field}
                onChange={(_, value) =>
                  handleUpdateCondition(index, { field: value as string })
                }
                placeholder="Select field"
              >
                {fieldOptions.length === 0 ? (
                  <Option value="" disabled>
                    No fields available (add a data source first)
                  </Option>
                ) : (
                  fieldOptions.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.dataSourceName}: {opt.label}
                    </Option>
                  ))
                )}
              </Select>
            </FormControl>

            {/* Operator selector */}
            <FormControl size="sm">
              <FormLabel>Operator</FormLabel>
              <Select
                value={condition.operator}
                onChange={(_, value) =>
                  handleUpdateCondition(index, {
                    operator: value as VisibilityOperator,
                  })
                }
              >
                {(Object.keys(OPERATOR_LABELS) as VisibilityOperator[]).map(
                  (op) => (
                    <Option key={op} value={op}>
                      {OPERATOR_LABELS[op]}
                    </Option>
                  ),
                )}
              </Select>
            </FormControl>

            {/* Value input (only if operator requires value) */}
            {!NO_VALUE_OPERATORS.includes(condition.operator) && (
              <FormControl size="sm">
                <FormLabel>Value</FormLabel>
                {["in", "not_in"].includes(condition.operator) ? (
                  <Input
                    value={
                      Array.isArray(condition.value)
                        ? condition.value.join(", ")
                        : String(condition.value || "")
                    }
                    onChange={(e) => {
                      const values = e.target.value
                        .split(",")
                        .map((v) => v.trim())
                        .filter(Boolean);
                      handleUpdateCondition(index, { value: values });
                    }}
                    placeholder="value1, value2, ..."
                  />
                ) : (
                  <Input
                    value={String(condition.value || "")}
                    onChange={(e) =>
                      handleUpdateCondition(index, { value: e.target.value })
                    }
                    placeholder="Enter value"
                  />
                )}
              </FormControl>
            )}
          </Stack>
        </Box>
      ))}

      {/* Add condition button */}
      <Button
        size="sm"
        variant="soft"
        color="neutral"
        startDecorator={<AddIcon />}
        onClick={handleAddCondition}
      >
        Add Condition
      </Button>

      {/* Clear rules button (if rules exist) */}
      {currentRules.conditions.length > 0 && (
        <Button
          size="sm"
          variant="plain"
          color="danger"
          onClick={handleClearRules}
        >
          Clear All Rules
        </Button>
      )}

      {/* Preview */}
      <Box sx={{ mt: 1 }}>
        <Chip size="sm" variant="soft" color="primary">
          {previewText}
        </Chip>
      </Box>
    </Stack>
  );
}

export default VisibilityRulesEditor;
